import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import BlockRenderer from "../components/BlockRenderer";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Standalone public page renderer — no auth, no app shell.
export default function PublicFunnel() {
  const { funnelId, pageSlug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const loadFirst = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/public/funnel/${funnelId}`);
      navigate(`/f/${funnelId}/${data.first_slug}`, { replace: true });
    } catch { setError("This funnel is not available."); }
  }, [funnelId, navigate]);

  const loadPage = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/public/funnel/${funnelId}/page/${pageSlug}`);
      setData(data);
      if (data.page?.seo?.title) document.title = data.page.seo.title;
    } catch { setError("Page not found."); }
  }, [funnelId, pageSlug]);

  useEffect(() => { if (!pageSlug) loadFirst(); else loadPage(); }, [pageSlug, loadFirst, loadPage]);

  const onSubmit = async (values, block) => {
    setSubmitting(true);
    try {
      const { data: res } = await axios.post(`${API}/public/funnel/${funnelId}/submit`, {
        page_id: data.page.id, block_id: block?.id, values,
      });
      toast.success("Thanks! You're all set.");
      if (res.redirect_slug) navigate(`/f/${funnelId}/${res.redirect_slug}`);
      else {
        const idx = data.pages.findIndex((p) => p.slug === pageSlug);
        const next = data.pages[idx + 1];
        if (next) navigate(`/f/${funnelId}/${next.slug}`);
      }
    } catch { toast.error("Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };

  if (error) return <div className="min-h-screen flex items-center justify-center bg-[#090D16] text-white/60">{error}</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-[#090D16]"><Loader2 className="w-6 h-6 animate-spin text-white/50" /></div>;

  const brand = data.brand?.color || "#6366F1";
  const bg = data.page?.settings?.bg || "#090D16";

  return (
    <div className="min-h-screen text-white" style={{ background: bg }}>
      <div className="max-w-4xl mx-auto px-5 py-16 sm:py-24 space-y-8" data-testid="public-funnel-page">
        {(data.page.blocks || []).map((b) => (
          <BlockRenderer key={b.id} block={b} brand={brand} onSubmit={onSubmit} submitting={submitting} />
        ))}
        <p className="text-center text-white/30 text-xs pt-8">Powered by {data.brand?.name || "Momento"}</p>
      </div>
    </div>
  );
}
